import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import API from "../../services/api";
import SetupIncompleteBanner from "../../components/common/SetupIncompleteBanner";
import OrgProfileBanner from "../../components/setup/OrgProfileBanner";
import BrixigoPageLoader from "../../components/common/BrixigoPageLoader";
import EmployeeList from "./EmployeeList";
import EmployeeBulkUploadUnified from "./EmployeeBulkUploadUnified";
import "../../styles/employeePages.css";

const VIEWS = {
  list: "list",
  add: "add",
  import: "import",
  edit: "edit",
};

const VIEW_META = {
  list: {
    title: "Employees",
    subtitle: "Manage your workforce — search, filter, add, and import records in one place.",
  },
  add: {
    title: "Add Employee",
    subtitle: "Guided 4-step wizard — personal details, org, compensation, and banking.",
  },
  import: {
    title: "Bulk Import",
    subtitle: "Upload CSV or Excel — same fields as Add Employee, with column mapping.",
  },
  edit: {
    title: "Edit Employee",
    subtitle: "Update employee record — all steps available for changes.",
  },
};

function ViewTab({ active, onClick, icon, title, desc }) {
  return (
    <button type="button" onClick={onClick} className={`emp-view-tab${active ? " active" : ""}`}>
      <span className="emp-view-tab-icon">{icon}</span>
      <span className="emp-view-tab-text">
        <span className="emp-view-tab-title">{title}</span>
        <span className="emp-view-tab-desc">{desc}</span>
      </span>
    </button>
  );
}

const IconUsers = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const IconUserPlus = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" />
    <line x1="20" y1="8" x2="20" y2="14" /><line x1="23" y1="11" x2="17" y2="11" />
  </svg>
);

const IconUpload = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

export default function EmployeesPage({ embedded = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const rawView = searchParams.get("view") || VIEWS.list;
  const view = rawView === VIEWS.add || rawView === VIEWS.edit ? VIEWS.list : rawView;
  const safeView = Object.values(VIEWS).includes(view) ? view : VIEWS.list;
  const needsBank = searchParams.get("needs_bank") === "1";

  const [listRefreshKey, setListRefreshKey] = useState(0);
  const [recordCount, setRecordCount] = useState(null);
  const [loadingLookups, setLoadingLookups] = useState(false);
  const [lookups, setLookups] = useState({
    departments: [],
    designations: [],
    locations: [],
  });

  const setView = useCallback(
    (nextView, extra = {}) => {
      const params = new URLSearchParams(searchParams);
      if (embedded) params.set("module", "employees");
      if (nextView !== VIEWS.list) params.set("view", nextView);
      else params.delete("view");
      if (extra.id) params.set("id", extra.id);
      else params.delete("id");
      // Keep bank-setup context when opening an employee from the missing-bank list.
      if (needsBank || extra.needsBank) {
        params.set("needs_bank", "1");
        params.set("step", String(extra.step || 4));
      } else if (extra.clearNeedsBank) {
        params.delete("needs_bank");
        params.delete("step");
      }
      setSearchParams(params, { replace: false });
    },
    [setSearchParams, searchParams, embedded, needsBank]
  );

  const openAddEmployee = useCallback(() => {
    navigate("/employees/add");
  }, [navigate]);

  const openEditEmployee = useCallback(
    (id, extra = {}) => {
      const q = new URLSearchParams();
      if (needsBank || extra.needsBank) {
        q.set("needs_bank", "1");
        q.set("step", String(extra.step || 4));
      }
      const qs = q.toString();
      // Return to this list (with its filters) after a successful save.
      navigate(`/employees/${id}/edit${qs ? `?${qs}` : ""}`, {
        state: { returnTo: `${location.pathname}${location.search}` },
      });
    },
    [navigate, needsBank, location.pathname, location.search]
  );

  const goToList = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    if (embedded) params.set("module", "employees");
    params.delete("view");
    params.delete("id");
    // Stay on the missing-bank queue so HR can open the next employee.
    if (needsBank) {
      params.set("needs_bank", "1");
      params.set("step", "4");
    } else {
      params.delete("needs_bank");
      params.delete("step");
    }
    setSearchParams(params, { replace: false });
    setListRefreshKey((k) => k + 1);
  }, [setSearchParams, searchParams, embedded, needsBank]);

  useEffect(() => {
    if (safeView !== VIEWS.import) return undefined;
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    setLoadingLookups(true);
    Promise.all([
      API.get("/setup/departments", { headers }),
      API.get("/setup/designations", { headers }),
      API.get("/setup/locations", { headers }),
    ])
      .then(([d, des, loc]) => {
        setLookups({
          departments: d.data || [],
          designations: des.data || [],
          locations: loc.data || [],
        });
      })
      .catch(() => {
        setLookups({ departments: [], designations: [], locations: [] });
      })
      .finally(() => setLoadingLookups(false));
  }, [safeView]);

  const meta = VIEW_META[safeView] || VIEW_META.list;

  const viewTabs = (
    <div className={`emp-view-tabs${embedded ? " emp-view-tabs--embedded" : ""}`}>
      <ViewTab
        active={safeView === VIEWS.list}
        onClick={() => setView(VIEWS.list)}
        icon={<IconUsers />}
        title="All Employees"
        desc="Search, filter & manage"
      />
      <ViewTab
        active={false}
        onClick={openAddEmployee}
        icon={<IconUserPlus />}
        title="Add Employee"
        desc="Single guided entry"
      />
      <ViewTab
        active={safeView === VIEWS.import}
        onClick={() => setView(VIEWS.import)}
        icon={<IconUpload />}
        title="Bulk Import"
        desc="CSV / Excel upload"
      />
    </div>
  );

  const content = (
    <>
      <SetupIncompleteBanner compact={safeView === VIEWS.list} />
      {safeView === VIEWS.import && (
        <div style={{ marginTop: 12 }}>
          <OrgProfileBanner />
        </div>
      )}

      <div className="emp-unified-content">
        {safeView === VIEWS.list && (
          <EmployeeList
            embedded
            listRefreshKey={listRefreshKey}
            onRecordCountChange={setRecordCount}
            onAddEmployee={openAddEmployee}
            onBulkImport={() => setView(VIEWS.import)}
            onEditEmployee={(id) => openEditEmployee(id, { needsBank, step: needsBank ? 4 : undefined })}
            missingVerifiedBankOnly={needsBank}
          />
        )}

        {safeView === VIEWS.import && (
          <div className="emp-unified-import">
            <div className="emp-import-toolbar">
              <Link to="/employee-import/guide" className="emp-btn-ghost">
                User Guide
              </Link>
            </div>
            {loadingLookups ? (
              <div className="emp-card">
                <BrixigoPageLoader message="Loading organisation data…" />
              </div>
            ) : (
              <EmployeeBulkUploadUnified
                lookups={lookups}
                onComplete={goToList}
              />
            )}
          </div>
        )}
      </div>
    </>
  );

  if (embedded) {
    return (
      <div className="emp-unified-embedded">
        {viewTabs}
        {content}
      </div>
    );
  }

  return (
    <div className="emp-page emp-unified-page">
      {/* Unified hero with view tabs */}
      <div className="emp-hero emp-unified-hero">
        <div className="emp-hero-inner">
          <div className="emp-unified-hero-text">
            <h1 className="emp-hero-title">{meta.title}</h1>
            <p className="emp-hero-sub">{meta.subtitle}</p>
          </div>
          {safeView === VIEWS.list && recordCount !== null && (
            <div className="emp-hero-stat">
              <IconUsers />
              {recordCount} active record{recordCount !== 1 ? "s" : ""}
            </div>
          )}
        </div>
        {viewTabs}
      </div>

      {content}
    </div>
  );
}
